import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import AppConst from '../apis/appconst';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * BalanceSheet — Module-wise pending dues view.
 * Self-contained: manages its own state, filters, fetching, search & print.
 *
 * Usage:
 *   import BalanceSheet from './BalanceSheet';
 *   <BalanceSheet />
 *
 * Optional props:
 *   - defaultFromDate, defaultToDate (YYYY-MM-DD strings)
 *   - onCountChange(count): callback whenever total due-entry count changes
 *                           (useful if parent wants to show count in a tab badge)
 */
function BalanceSheet({ defaultFromDate, defaultToDate, onCountChange }) {
    

    // Default: today's date only. User can change manually if needed.
    const getLocalToday = () => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };
    
    const [fromDate, setFromDate] = useState(getLocalToday());
    const [toDate, setToDate] = useState(getLocalToday());

    const [balanceData, setBalanceData] = useState({
        ipd: [],
        ipd_bill: [],
        opd: [],
        lab: [],
        pharmacy: [],
        emergency: []
    });
    const [isLoading, setIsLoading] = useState(false);
    const [searchKey, setSearchKey] = useState('');
    const [expandedModules, setExpandedModules] = useState({
        ipd: true,
        ipd_bill: true,
        opd: true,
        lab: true,
        pharmacy: true,
        emergency: true
    });

    const moduleConfig = {
        ipd:       { label: 'IPD Registration', color: 'primary',   icon: 'ri-hospital-line' },
        ipd_bill:  { label: 'IPD Billing',      color: 'primary',   icon: 'ri-file-list-3-line' },
        opd:       { label: 'OPD',              color: 'success',   icon: 'ri-user-heart-line' },
        lab:       { label: 'Lab',              color: 'warning',   icon: 'ri-test-tube-line' },
        pharmacy:  { label: 'Pharmacy',         color: 'secondary', icon: 'ri-capsule-line' },
        emergency: { label: 'Emergency',        color: 'danger',    icon: 'ri-alarm-warning-line' },
    };

    // ========== FETCH ==========
    const fetchBalanceSheet = useCallback(async () => {
        setIsLoading(true);
        try {
            const fd = new FormData();
            fd.append('from_date', fromDate);
            fd.append('to_date', toDate);

            const response = await axios.post(
                AppConst.BASE_URL + AppConst.getBalanceSheetUrl,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            if (response?.data?.status) {
                setBalanceData(response.data.balance_data || {
                    ipd: [], ipd_bill: [], opd: [], lab: [], pharmacy: [], emergency: []
                });
            } else {
                toast.error(response?.data?.message || 'Failed to fetch balance sheet');
                setBalanceData({ ipd: [], ipd_bill: [], opd: [], lab: [], pharmacy: [], emergency: [] });
            }
        } catch (error) {
            console.error('Fetch balance sheet error:', error);
            toast.error('Failed to fetch balance sheet');
            setBalanceData({ ipd: [], ipd_bill: [], opd: [], lab: [], pharmacy: [], emergency: [] });
        } finally {
            setIsLoading(false);
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        fetchBalanceSheet();
    }, [fetchBalanceSheet]);

    // ========== FILTERS ==========
    const filteredBalanceData = useMemo(() => {
        if (!searchKey.trim()) return balanceData;
        const search = searchKey.toLowerCase();
        const filtered = {};
        Object.keys(balanceData).forEach(module => {
            filtered[module] = (balanceData[module] || []).filter(item =>
                (item.uhid || '').toString().toLowerCase().includes(search) ||
                (item.patient_name || '').toLowerCase().includes(search) ||
                (item.reg_no || '').toString().toLowerCase().includes(search) ||
                (item.bill_no || '').toString().toLowerCase().includes(search) ||
                (item.user_name || '').toLowerCase().includes(search)
            );
        });
        return filtered;
    }, [balanceData, searchKey]);

    const moduleBalanceTotals = useMemo(() => {
        const totals = {};
        Object.keys(filteredBalanceData).forEach(module => {
            totals[module] = (filteredBalanceData[module] || []).reduce(
                (sum, item) => sum + Number(item.balance || 0), 0
            );
        });
        return totals;
    }, [filteredBalanceData]);

    const grandTotalBalance = useMemo(
        () => Object.values(moduleBalanceTotals).reduce((sum, v) => sum + v, 0),
        [moduleBalanceTotals]
    );

    const totalDueCount = useMemo(
        () => Object.values(filteredBalanceData).reduce((s, a) => s + (a?.length || 0), 0),
        [filteredBalanceData]
    );

    // Notify parent of count changes
    useEffect(() => {
        if (typeof onCountChange === 'function') onCountChange(totalDueCount);
    }, [totalDueCount, onCountChange]);

    // ========== HELPERS ==========
    const formatDate = (d) => {
        if (!d) return 'N/A';
        const dt = new Date(d);
        if (isNaN(dt)) return d;
        return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatDateTime = (d) => {
        if (!d) return 'N/A';
        const dt = new Date(d);
        if (isNaN(dt)) return d;
        return dt.toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: true
        });
    };

    const toggleModule = (mod) => {
        setExpandedModules(prev => ({ ...prev, [mod]: !prev[mod] }));
    };

    const handleClearSearch = () => setSearchKey('');

    // ========== PDF ==========
    const handlePrintPDF = () => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('BALANCE SHEET - PENDING DUES', pageWidth / 2, 15, { align: 'center' });

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const dateLine = fromDate === toDate
            ? `Date: ${formatDate(toDate)}`
            : `From: ${formatDate(fromDate)}    To: ${formatDate(toDate)}`;
        doc.text(dateLine, pageWidth / 2, 22, { align: 'center' });

        let y = 30;

        Object.keys(moduleConfig).forEach(modKey => {
            const items = filteredBalanceData[modKey] || [];
            if (items.length === 0) return;

            const cfg = moduleConfig[modKey];

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text(`${cfg.label} (${items.length})`, 14, y);
            doc.text(`Rs.${(moduleBalanceTotals[modKey] || 0).toFixed(2)}`, pageWidth - 14, y, { align: 'right' });
            y += 3;

            const body = items.map((it, idx) => [
                idx + 1,
                (it.patient_name || 'N/A').substring(0, 22),
                it.uhid || '-',
                it.reg_no || '-',
                it.bill_no || '-',
                formatDate(it.date),
                (it.user_name || 'N/A').substring(0, 12),
                (it.remarks || '-').substring(0, 20),
                `Rs.${Number(it.balance || 0).toFixed(2)}`
            ]);

            autoTable(doc, {
                startY: y,
                head: [['#', 'Patient', 'UHID', 'Reg No', 'Bill No', 'Date', 'User', 'Remarks', 'Balance']],
                body: body,
                theme: 'grid',
                headStyles: { fillColor: [64, 81, 137], fontSize: 7 },
                styles: { fontSize: 7, cellPadding: 1.2 },
                columnStyles: { 8: { halign: 'right' } }
            });

            y = doc.lastAutoTable.finalY + 6;

            if (y > 260) {
                doc.addPage();
                y = 15;
            }
        });

        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(220, 53, 69);
        doc.text(`GRAND TOTAL BALANCE: Rs.${grandTotalBalance.toFixed(2)}`, pageWidth / 2, y + 5, { align: 'center' });

        const pdfBlob = doc.output('blob');
        window.open(URL.createObjectURL(pdfBlob), '_blank');
    };

    // ========== RENDER ==========
    return (
        <div className="balance-sheet-wrapper">
            {/* Header / Print */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <small className="text-muted">
                    <i className="ri-hand-coin-line me-1"></i>
                    {fromDate === toDate ? (
                        <>Showing pending dues for <strong>{formatDate(toDate)}</strong></>
                    ) : (
                        <>Showing pending dues from <strong>{formatDate(fromDate)}</strong> to <strong>{formatDate(toDate)}</strong></>
                    )}
                </small>
                <button className="btn btn-primary btn-sm" onClick={handlePrintPDF} disabled={isLoading || totalDueCount === 0}>
                    <i className="ri-printer-line me-1"></i> Print Balance Sheet
                </button>
            </div>

            {/* Filters */}
            <div className="card mb-3">
                <div className="card-body py-2">
                    <div className="row g-2 align-items-end">
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small mb-1">From Date</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <label className="form-label small mb-1">To Date</label>
                            <input
                                type="date"
                                className="form-control form-control-sm"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </div>
                        <div className="col-lg-2 col-md-4 col-6">
                            <button
                                className="btn btn-primary btn-sm w-100"
                                onClick={fetchBalanceSheet}
                                disabled={isLoading}
                            >
                                {isLoading
                                    ? <span className="spinner-border spinner-border-sm"></span>
                                    : <><i className="ri-refresh-line me-1"></i>Refresh</>}
                            </button>
                        </div>
                        <div className="col-lg-6">
                            <label className="form-label small mb-1">Search</label>
                            <div className="input-group input-group-sm">
                                <span className="input-group-text bg-light">
                                    <i className="ri-search-line"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="🔍 Search by UHID, Patient Name, Reg No, Bill No, User..."
                                    value={searchKey}
                                    onChange={(e) => setSearchKey(e.target.value)}
                                />
                                {searchKey && (
                                    <button className="btn btn-outline-secondary" type="button" onClick={handleClearSearch}>
                                        <i className="ri-close-line"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Module Summary Cards */}
            <div className="row mb-3 g-2">
                {Object.keys(moduleConfig).map(modKey => {
                    const cfg = moduleConfig[modKey];
                    const total = moduleBalanceTotals[modKey] || 0;
                    const count = (filteredBalanceData[modKey] || []).length;
                    const borderClass = cfg.color === 'warning' ? 'border-warning' : `border-${cfg.color}`;
                    const textClass = cfg.color === 'warning' ? 'text-warning' : `text-${cfg.color}`;
                    return (
                        <div key={modKey} className="col-md-2 col-6">
                            <div className={`card ${borderClass} h-100`}>
                                <div className="card-body text-center py-2">
                                    <small className="text-muted">
                                        <i className={`${cfg.icon} me-1`}></i>{cfg.label}
                                    </small>
                                    <h6 className={`${textClass} mb-0 fw-bold`}>₹{total.toFixed(2)}</h6>
                                    <small className="text-muted" style={{ fontSize: '10px' }}>{count} entries</small>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Grand Total banner */}
            <div className="card bg-danger text-white mb-3">
                <div className="card-body py-2 d-flex justify-content-between align-items-center">
                    <div>
                        <i className="ri-hand-coin-line me-2" style={{ fontSize: '20px' }}></i>
                        <strong>TOTAL PENDING DUE</strong>
                        <span className="ms-2 badge bg-white text-danger">{totalDueCount} entries</span>
                    </div>
                    <h4 className="mb-0 text-white">₹{grandTotalBalance.toFixed(2)}</h4>
                </div>
            </div>

            {/* Tables */}
            {isLoading ? (
                <div className="card">
                    <div className="card-body text-center py-5">
                        <div className="spinner-border text-primary"></div>
                        <p className="mt-2 text-muted mb-0">Loading balance sheet...</p>
                    </div>
                </div>
            ) : totalDueCount === 0 ? (
                <div className="card">
                    <div className="card-body text-center py-5">
                        <i className="ri-checkbox-circle-line text-success" style={{ fontSize: '48px' }}></i>
                        <h5 className="mt-2 text-success">All Clear!</h5>
                        <p className="text-muted mb-0">
                            {searchKey
                                ? `No pending dues found for "${searchKey}"`
                                : 'No pending dues found in the selected date range.'}
                        </p>
                    </div>
                </div>
            ) : (
                Object.keys(moduleConfig).map(modKey => {
                    const items = filteredBalanceData[modKey] || [];
                    if (items.length === 0) return null;

                    const cfg = moduleConfig[modKey];
                    const isExpanded = expandedModules[modKey];
                    const moduleTotal = moduleBalanceTotals[modKey] || 0;
                    const badgeClass = cfg.color === 'warning' ? 'bg-warning text-dark' : `bg-${cfg.color}`;

                    return (
                        <div key={modKey} className="card mb-3">
                            <div
                                className="card-header d-flex justify-content-between align-items-center py-2"
                                style={{ cursor: 'pointer', backgroundColor: '#f8f9fa' }}
                                onClick={() => toggleModule(modKey)}
                            >
                                <div>
                                    <i className={`${isExpanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} me-1`}></i>
                                    <span className={`badge ${badgeClass} me-2`}>
                                        <i className={`${cfg.icon} me-1`}></i>{cfg.label}
                                    </span>
                                    <span className="text-muted small">{items.length} pending</span>
                                </div>
                                <div>
                                    <strong className="text-danger">₹{moduleTotal.toFixed(2)}</strong>
                                </div>
                            </div>

                            {isExpanded && (
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover table-striped mb-0" style={{ fontSize: '11px' }}>
                                            <thead className="table-dark">
                                                <tr>
                                                    <th style={{ width: '40px' }}>#</th>
                                                    <th>Patient Name</th>
                                                    <th>UHID</th>
                                                    <th>Reg No</th>
                                                    <th>Bill No</th>
                                                    <th>Date</th>
                                                    <th>User</th>
                                                    <th>Remarks</th>
                                                    <th className="text-end">Balance (₹)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((item, i) => (
                                                    <tr key={`${modKey}_${item.id || i}`}>
                                                        <td>{i + 1}</td>
                                                        <td><strong>{item.patient_name || 'N/A'}</strong></td>
                                                        <td><span className="text-primary">{item.uhid || '-'}</span></td>
                                                        <td>{item.reg_no || '-'}</td>
                                                        <td>{item.bill_no || '-'}</td>
                                                        <td>{formatDateTime(item.date)}</td>
                                                        <td>{item.user_name || 'N/A'}</td>
                                                        <td>
                                                            <span title={item.remarks || ''}>
                                                                {item.remarks
                                                                    ? (item.remarks.length > 30
                                                                        ? item.remarks.substring(0, 30) + '...'
                                                                        : item.remarks)
                                                                    : '-'}
                                                            </span>
                                                        </td>
                                                        <td className="text-end fw-bold text-danger">
                                                            ₹{Number(item.balance || 0).toFixed(2)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot className="table-light">
                                                <tr>
                                                    <td colSpan="8" className="text-end fw-bold">Subtotal:</td>
                                                    <td className="text-end text-danger fw-bold">₹{moduleTotal.toFixed(2)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })
            )}

            {/* Final Grand Total */}
            {totalDueCount > 0 && (
                <div className="card bg-dark text-white mb-3">
                    <div className="card-body py-3 d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <i className="ri-money-rupee-circle-line me-2"></i>
                            GRAND TOTAL OUTSTANDING
                        </h5>
                        <h3 className="mb-0 text-warning">₹{grandTotalBalance.toFixed(2)}</h3>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BalanceSheet;